// src/pages/PlatformImpersonate.js
import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

function safeString(v) {
  return typeof v === "string" ? v : v == null ? "" : String(v);
}

function normalizePlatformRole(v) {
  const r = safeString(v).trim().toUpperCase();
  if (r === "SUPER_ADMIN" || r === "ADMIN" || r === "SUPPORT") return r;
  return "";
}

export default function PlatformImpersonate() {
  const nav = useNavigate();
  const auth = useAuth();

  const token = safeString(auth?.token).trim();
  const isAuthed = !!token;

  const mode = safeString(auth?.mode).toLowerCase().trim();
  const inPlatformMode = mode === "platform";

  const platformRole = normalizePlatformRole(auth?.user?.platformRole ?? auth?.platformRole ?? "");
  const hasPlatformAccess = !!platformRole;

  // Placeholder: until backend exists
  const users = useMemo(() => [], []);
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      return (
        String(u.name || "").toLowerCase().includes(q) ||
        String(u.email || "").toLowerCase().includes(q) ||
        String(u.storeName || "").toLowerCase().includes(q) ||
        String(u.storeId || "").toLowerCase().includes(q)
      );
    });
  }, [users, query]);

  const onStart = () => alert("Next: POST /api/platform/impersonate (start audited session).");
  const onEnd = () => alert("Next: POST /api/platform/impersonate/end (end session).");

  // -------------------------
  // Guards (match other platform pages)
  // -------------------------
  if (!isAuthed) {
    return (
      <div className="ft-page">
        <div className="ft-header">
          <div className="ft-header__text">
            <h1 className="ft-title">Impersonate</h1>
            <p className="ft-subtitle">Please sign in to continue.</p>
          </div>
          <div className="ft-header__actions">
            <button className="ft-btn ft-btn--primary" type="button" onClick={() => nav("/login")}>
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!hasPlatformAccess) {
    return (
      <div className="ft-page">
        <div className="ft-header">
          <div className="ft-header__text">
            <h1 className="ft-title">Impersonate</h1>
            <p className="ft-subtitle">You don’t have Platform access on this account.</p>
          </div>
          <div className="ft-header__actions">
            <button className="ft-btn ft-btn--ghost" type="button" onClick={() => nav("/unauthorized")}>
              Unauthorized
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!inPlatformMode) {
    return (
      <div className="ft-page">
        <div className="ft-header">
          <div className="ft-header__text">
            <h1 className="ft-title">Impersonate</h1>
            <p className="ft-subtitle">
              Switch to <b>Platform</b> mode using the sidebar toggle to use support tools.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // -------------------------
  // Main UI (placeholder)
  // -------------------------
  return (
    <div className="ft-page">
      <div className="ft-header">
        <div className="ft-header__text">
          <h1 className="ft-title">Impersonate</h1>
          <p className="ft-subtitle">
            Start an audited support session to see what a user sees. (No test data yet — will populate once the
            backend endpoints exist.)
          </p>
        </div>
      </div>

      <div className="ft-grid" style={{ gridTemplateColumns: "1.2fr 0.8fr" }}>
        {/* Left */}
        <div className="ft-card">
          <div className="ft-card__header">
            <div className="ft-card__headerTitle">Find a user</div>
            <div className="ft-card__headerSub">Search across stores, then start a session.</div>
          </div>

          <div className="ft-card__body">
            <div className="ft-controls">
              <input
                className="ft-input ft-input--grow"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search name, email, store, store ID…"
              />
              <button type="button" className="ft-btn ft-btn--primary" onClick={onStart} disabled>
                Start session
              </button>
            </div>

            <div className="ft-subtle" style={{ marginTop: 10 }}>
              {filtered.length === 0
                ? "No users found. Once you create users via onboarding, they’ll appear here."
                : "Select a user to start an impersonation session."}
            </div>

            {/* When backend is wired, this is where the selectable list will render */}
          </div>
        </div>

        {/* Right */}
        <div className="ft-card">
          <div className="ft-card__header">
            <div className="ft-card__headerTitle">Active session</div>
            <div className="ft-card__headerSub">Who you’re currently impersonating.</div>
          </div>

          <div className="ft-card__body">
            <div className="ft-subtle">No active session.</div>

            <div className="ft-actions" style={{ marginTop: 10 }}>
              <button type="button" className="ft-btn ft-btn--ghost" onClick={onEnd} disabled>
                End session
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}